import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged, signInWithPopup, signOut, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail, updateProfile } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from './firebase';
import { setCurrentUserId } from './utils/firebaseStorage';
import { isDevMode, getDevUser, logDevMode } from './utils/devUser';
import { createUserProfile, getUserProfile } from './services/profileService';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  isDevMode: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const devModeActive = isDevMode();

  const signInWithGoogle = async (): Promise<void> => {
    if (devModeActive) {
      console.log('🚀 Dev mode: Simulating Google sign-in');
      return;
    }

    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  const signInWithEmail = async (email: string, password: string): Promise<void> => {
    if (devModeActive) {
      console.log('🚀 Dev mode: Simulating email sign-in');
      return;
    }

    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Error signing in with email:', error);
      throw error;
    }
  };

  const signUpWithEmail = async (email: string, password: string, displayName: string): Promise<void> => {
    if (devModeActive) {
      console.log('🚀 Dev mode: Simulating email sign-up');
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      // Update the user's display name
      if (userCredential.user) {
        await updateProfile(userCredential.user, { displayName });

        // Trigger verification email
        fetch('/.netlify/functions/send-verification-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: userCredential.user.email })
        }).then(res => {
          if (res.ok) {
            console.log('[Auth] Verification email sent successfully');
          } else {
            console.error('[Auth] Failed to send verification email:', res.status);
          }
        }).catch(err => {
          console.error('[Auth] Error sending verification email:', err);
        });
      }
    } catch (error) {
      console.error('Error signing up with email:', error);
      throw error;
    }
  };

  const resetPassword = async (email: string): Promise<void> => {
    if (devModeActive) {
      console.log('🚀 Dev mode: Simulating password reset');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    if (devModeActive) {
      console.log('🚀 Dev mode: Simulating logout');
      return;
    }

    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  useEffect(() => {
    // Log dev mode status
    logDevMode();

    if (devModeActive) {
      // In dev mode, use mock user
      const devUser = getDevUser();
      setCurrentUser(devUser);
      setCurrentUserId(devUser.uid);
      setLoading(false);
      return;
    }

    // Normal Firebase auth flow
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      // Set user ID for Firebase storage (enables cross-device sync)
      if (user) {
        setCurrentUserId(user.uid);

        // Handle welcome email for new users (non-blocking)
        try {
          const userProfile = await getUserProfile(user.uid);

          if (!userProfile) {
            // New user - create profile and send welcome email
            console.log('[Auth] New user detected, creating profile and sending welcome email');
            await createUserProfile(user);

            // Send welcome email asynchronously
            fetch('/.netlify/functions/send-welcome-email', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: user.email,
                displayName: user.displayName,
                uid: user.uid
              })
            }).then(res => {
              if (res.ok) {
                console.log('[Auth] Welcome email sent successfully');
              } else {
                console.error('[Auth] Failed to send welcome email:', res.status);
              }
            }).catch(err => {
              console.error('[Auth] Error sending welcome email:', err);
            });

            // Update isFirstLogin to false
            const userDocRef = doc(db, 'users', user.uid);
            await updateDoc(userDocRef, { isFirstLogin: false });

          } else if (userProfile.isFirstLogin) {
            // Existing user but first login flag still true - send welcome email
            console.log('[Auth] First login detected, sending welcome email');

            // Send welcome email asynchronously
            fetch('/.netlify/functions/send-welcome-email', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: user.email,
                displayName: user.displayName,
                uid: user.uid
              })
            }).then(res => {
              if (res.ok) {
                console.log('[Auth] Welcome email sent successfully');
              } else {
                console.error('[Auth] Failed to send welcome email:', res.status);
              }
            }).catch(err => {
              console.error('[Auth] Error sending welcome email:', err);
            });

            // Update isFirstLogin to false
            const userDocRef = doc(db, 'users', user.uid);
            await updateDoc(userDocRef, { isFirstLogin: false });
          }
        } catch (error) {
          // Don't block login if welcome email fails
          console.error('[Auth] Error handling welcome email:', error);
        }
      } else {
        setCurrentUserId(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, [devModeActive]);

  const value: AuthContextType = {
    currentUser,
    loading,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    resetPassword,
    logout,
    isDevMode: devModeActive,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}